import React from 'react'
import moment from 'moment'
import 'moment/locale/cs'
import { ArrowRight, Lock } from 'lucide-react'
import { isSimilarityMethod } from '../../helpers/valuationMethods'
import { formatComparableDaysOnMarket, getComparableMarketStatus } from '../../helpers/comparableMarketStatus'

const formatPrice = (value) => (Number.isFinite(value) ? Math.round(value).toLocaleString() : '—')
const formatPercent = (value) => (Number.isFinite(value) ? `${Math.round(value * 100)} %` : '—')
const formatPriceAdjustment = (factor) => {
  if (!Number.isFinite(factor)) return '—'
  const percentage = (factor - 1) * 100
  if (Math.abs(percentage) < 0.05) return '0%'
  return `${percentage > 0 ? '+' : ''}${percentage.toFixed(1)}%`
}

const getImageUrl = (property) => {
  if (property.imageUrl) return property.imageUrl
  const image = property.gallery?.[0]
  return typeof image === 'string' ? image : image?.url || image?.src || null
}

const getRelevanceTooltip = (property) => {
  const relevance = property.relevanceWeight ?? property.timeWeight
  const calculatedRelevance = property.calculatedRelevanceWeight ?? relevance
  const factors = property.weightFactors
  const multiplicative = property.relevanceMethod === 'multiplicative'
  const floorRuleDetails = {
    exact_low_condition: 'Spodní hranice: silné zvýhodnění přesného špatného stavu.',
    good_condition_local_price: `Spodní hranice: stav V dobrém stavu je vážen podle místní ceny · levnost ${formatPercent(
      1 - (property.floorConditionLocalPricePosition ?? 0.5)
    )}.`,
    penalized_better_condition: 'Spodní hranice: lepší stav je silně potlačen.',
  }

  if (!factors) {
    return `Váha stáří nabídky: ${formatPercent(relevance)}\nŽebříčková metoda nemá společné skóre podobnosti.`
  }

  const factorDetails = [
    '',
    `Aktuálnost: ${formatPercent(factors.time)}`,
    `Vzdálenost: ${formatPercent(factors.distance)}`,
    `Podobnost výměry: ${formatPercent(factors.area)}`,
    `Podobnost dispozice: ${formatPercent(factors.layout)}`,
    `Podobnost stavu: ${formatPercent(factors.condition)}`,
    ...(property.floorConditionRule ? [floorRuleDetails[property.floorConditionRule]] : []),
    '',
    `Průměr faktorů: ${formatPercent(property.arithmeticRelevanceWeight ?? calculatedRelevance)}`,
    `Součin faktorů: ${formatPercent(property.multiplicativeRelevanceWeight)}`,
    '',
    multiplicative
      ? `Použitá automatická váha = aktuálnost × vzdálenost × výměra × dispozice × stav${property.floorConditionRule ? ', nejvýše 100 %' : ''}.`
      : `Použitá automatická váha = (aktuálnost + vzdálenost + výměra + dispozice + stav) ÷ 5${property.floorConditionRule ? ', nejvýše 100 %' : ''}.`,
  ]

  if (Number.isFinite(property.manualWeightOverride)) {
    return [
      `Použitá ruční váha: ${formatPercent(relevance)}`,
      `Automaticky vypočtená váha: ${formatPercent(calculatedRelevance)}`,
      ...factorDetails,
      '',
      'Ruční váha nahrazuje automaticky vypočtený průměr faktorů.',
    ].join('\n')
  }

  return [`Automaticky vypočtená váha: ${formatPercent(relevance)}`, ...factorDetails].join('\n')
}

const LockedComparableRows = ({ count }) => {
  const rowCount = Math.min(Math.max(Number(count) || 0, 0), 12)
  if (!rowCount) return null

  return (
    <div className="relative h-[360px] overflow-hidden border-t border-violet-100 bg-gradient-to-b from-white via-violet-50/35 to-lime-50/55">
      <div aria-hidden="true" className="divide-y divide-gray-100">
        {Array.from({ length: rowCount }, (_, index) => (
          <div
            key={index}
            className="grid h-16 grid-cols-[1.45fr_.38fr_.48fr_.62fr] items-center gap-3 px-3 sm:grid-cols-[1.8fr_.45fr_.55fr_.8fr_1fr]"
            style={{
              filter: `blur(${Math.min(0.7 + index * 0.34, 4)}px)`,
              opacity: Math.max(0.16, 0.72 - index * 0.045),
            }}
          >
            <div className="flex items-center gap-3">
              <span className="h-10 w-14 shrink-0 rounded-lg bg-gray-200" />
              <span className="h-3 w-full max-w-48 rounded-full bg-gray-200" />
            </div>
            <span className="h-3 rounded-full bg-gray-200" />
            <span className="h-3 rounded-full bg-gray-200" />
            <span className="h-3 rounded-full bg-gray-200" />
            <span className="hidden h-3 rounded-full bg-gray-200 sm:block" />
          </div>
        ))}
      </div>

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.28)_42%,rgba(255,255,255,0.9)_100%)]" />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="flex min-h-[230px] w-[min(94%,540px)] flex-col items-center justify-center rounded-[999px] border border-violet-200/80 bg-white/80 px-7 py-8 text-center shadow-[0_24px_70px_rgba(91,33,182,0.16)] backdrop-blur-xl sm:px-12">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-600 text-white shadow-lg shadow-violet-600/20">
            <Lock className="h-4 w-4" aria-hidden="true" />
          </span>
          <h3 className="mt-4 text-base font-semibold text-gray-950 sm:text-lg">
            + {count.toLocaleString('cs-CZ')} dalších srovnatelných nabídek
          </h3>
          <p className="mt-2 max-w-md text-xs leading-relaxed text-gray-600 sm:text-sm">
            Odemkněte kompletní srovnání, cenové úpravy a vlastní investiční scénáře.
          </p>
          <a
            href="https://www.realitni-jestrab.cz/?signup=1&utm_source=property-demo#cena"
            className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-gray-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
          >
            Získat plný přístup
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  )
}

const ComparableTable = ({
  properties = [],
  subjectM2,
  onIgnoreProperty,
  onWeightOverride,
  onOpenProperty,
  onHoverProperty,
  calculationMethod,
  recalculatingPropertyId,
  showDetailLinks = true,
  lockedComparablesCount = 0,
}) => {
  const hasComparisonActions = Boolean(onIgnoreProperty || (isSimilarityMethod(calculationMethod) && onWeightOverride))
  const hasDetailActions = showDetailLinks && properties.some((property) => property.propertyId)
  const showActionsColumn = hasComparisonActions || hasDetailActions
  const sortedProperties = [...properties].sort((a, b) => {
    const relevanceDifference = (b.relevanceWeight ?? b.relevanceScore ?? b.timeWeight ?? 0) - (a.relevanceWeight ?? a.relevanceScore ?? a.timeWeight ?? 0)
    if (relevanceDifference) return relevanceDifference
    return new Date(b.created).getTime() - new Date(a.created).getTime()
  })

  if (!sortedProperties.length) {
    return <p className="text-xs sm:text-sm text-gray-500 italic">Nejsou k dispozici žádné použité nabídky.</p>
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <div className="overflow-x-auto">
        <table
          className="w-full table-fixed divide-y divide-gray-200 text-xs"
          style={{ minWidth: hasComparisonActions ? '1180px' : showActionsColumn ? '1080px' : '1000px' }}
        >
        <colgroup>
          <col style={{ width: '2.5%' }} />
          <col style={{ width: hasComparisonActions ? '25%' : '30%' }} />
          <col style={{ width: hasComparisonActions ? '6%' : '6.5%' }} />
          <col style={{ width: hasComparisonActions ? '6.5%' : '7%' }} />
          <col style={{ width: hasComparisonActions ? '5%' : '5.5%' }} />
          <col style={{ width: hasComparisonActions ? '12.5%' : '13%' }} />
          <col style={{ width: '21%' }} />
          <col style={{ width: '9%' }} />
          {showActionsColumn && <col style={{ width: hasComparisonActions ? '12.5%' : '5.5%' }} />}
        </colgroup>
        <thead className="bg-gray-50 text-left text-gray-600">
          <tr>
            <th className="px-2 py-2 font-medium">#</th>
            <th className="px-2 py-2 font-medium">Nabídka</th>
            <th className="px-2 py-2 font-medium">Váha</th>
            <th className="px-2 py-2 font-medium">Vzdál.</th>
            <th className="px-2 py-2 font-medium">m²</th>
            <th className="px-3 py-2 font-medium">Cena/m²</th>
            <th className="px-3 py-2 font-medium">Po úpravách</th>
            <th className="px-3 py-2 font-medium">Datum</th>
            {showActionsColumn && (
              <th
                data-pdf-ignore="true"
                className="sticky right-0 z-10 border-l border-gray-200 bg-gray-50 px-3 py-2 font-medium"
              >
                Akce
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {sortedProperties.map((property, index) => {
            const imageUrl = getImageUrl(property)
            const marketStatus = getComparableMarketStatus(property)
            const createdAt = moment(property.created)
            const relevanceTooltip = getRelevanceTooltip(property)
            const combinedPriceAdjustmentFactor =
              Number.isFinite(property.adjustedM2Price) && Number.isFinite(property.m2Price) && property.m2Price > 0
                ? property.adjustedM2Price / property.m2Price
                : null
            return (
              <tr
                key={property.propertyId || index}
                onMouseEnter={() => onHoverProperty?.(property.propertyId?.toString() || null)}
                onMouseLeave={() => onHoverProperty?.(null)}
                className="group align-top hover:bg-gray-50"
              >
                <td className="whitespace-nowrap px-2 py-3 font-semibold text-gray-500">{index + 1}</td>
                <td className="px-2 py-3">
                  <div className="flex gap-3">
                    {imageUrl ? (
                      <button type="button" onClick={() => onOpenProperty?.(property)} className="shrink-0">
                        <img
                          src={imageUrl}
                          alt=""
                          loading="lazy"
                          className="h-24 w-32 rounded-lg object-cover bg-gray-100 transition-opacity hover:opacity-80"
                        />
                      </button>
                    ) : (
                      <div className="flex h-24 w-32 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-[10px] text-gray-400">Bez fotografie</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <button type="button" onClick={() => onOpenProperty?.(property)} className="block text-left font-medium text-gray-900 hover:text-blue-700">
                        {property.title || 'Nabídka bez názvu'}
                      </button>
                      <div className="mt-1 text-gray-500">{property.location}</div>
                      <div className="mt-1 text-gray-500">
                        {[property.layout, property.condition].filter(Boolean).join(' · ')}
                      </div>
                      {property.hasComparableOverride && <div className="mt-1 text-[10px] font-medium text-blue-700">Upraveno pro tento odhad</div>}
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-2 py-3">
                  <span
                    title={relevanceTooltip}
                    className="cursor-help border-b border-dotted border-blue-400 font-semibold text-blue-700"
                    aria-label={relevanceTooltip}
                  >
                    {formatPercent(property.relevanceWeight ?? property.timeWeight)}
                  </span>
                  {Number.isFinite(property.manualWeightOverride) && <div className="mt-1 text-[10px] font-medium text-blue-700">Ručně</div>}
                </td>
                <td className="whitespace-nowrap px-2 py-3 text-gray-600">
                  {Number.isFinite(property.distanceMeters) ? `${Math.round(property.distanceMeters)} m` : '—'}
                </td>
                <td className="whitespace-nowrap px-2 py-3 text-gray-600">
                  <div>{property.m2} m²</div>
                  {property.sourceValues && Number(property.sourceValues.m2) !== Number(property.m2) && (
                    <div className="mt-1 text-[10px] text-gray-400">Zdroj: {property.sourceValues.m2} m²</div>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-gray-900">
                  <div>{formatPrice(property.sourceM2Price ?? property.m2Price)} Kč/m²</div>
                  {Number.isFinite(property.sourceM2Price) && Math.round(property.sourceM2Price) !== Math.round(property.m2Price) && (
                    <div className="mt-1 text-[10px] text-blue-700">Po změně výměry: {formatPrice(property.m2Price)} Kč/m²</div>
                  )}
                  {Number.isFinite(property.price) && (
                    <div className="mt-1 text-[10px] text-gray-500">
                      <div>Nabídková cena:</div>
                      <div>{formatPrice(property.price)} Kč</div>
                    </div>
                  )}
                </td>
                <td className="whitespace-normal px-3 py-3 font-semibold text-gray-900">
                  {property.adjustedM2Price ? (
                    <>
                      <div className="whitespace-nowrap">
                        {formatPrice(property.adjustedM2Price)} Kč/m²
                        {Number.isFinite(combinedPriceAdjustmentFactor) && <> · {formatPriceAdjustment(combinedPriceAdjustmentFactor)}</>}
                      </div>
                      {(Number.isFinite(property.areaAdjustmentFactor) || Number.isFinite(property.floorAdjustmentFactor)) && (
                        <div className="mt-1 space-y-0.5 text-[10px] font-normal text-gray-500">
                          <div>Rychlost: {formatPriceAdjustment(property.marketSpeedAdjustmentFactor)}</div>
                          <div>
                            Výměra: {formatPriceAdjustment(property.areaAdjustmentFactor)} · Podlaží: {formatPriceAdjustment(property.floorAdjustmentFactor)}
                          </div>
                        </div>
                      )}
                      {Number.isFinite(Number(subjectM2)) && (
                        <div className="mt-1 text-[10px] font-normal text-gray-500">
                          <div>Pro oceňovaných {Number(subjectM2).toLocaleString('cs-CZ')} m²:</div>
                          <div>{formatPrice(property.adjustedM2Price * Number(subjectM2))} Kč</div>
                        </div>
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-gray-600">
                  <div>{createdAt.isValid() ? createdAt.format('DD.MM.YYYY') : '—'}</div>
                  {createdAt.isValid() && <div className="mt-1 text-[10px] text-gray-500">{createdAt.locale('cs').fromNow()}</div>}
                  <div
                    className={`mt-2 inline-flex rounded-full px-2 py-1 text-[10px] font-medium ${marketStatus.badgeClassName}`}
                  >
                    {marketStatus.label}
                  </div>
                  <div className={`mt-1 text-[10px] font-medium ${marketStatus.inactive ? 'text-red-700' : 'text-green-700'}`}>
                    {formatComparableDaysOnMarket(marketStatus.daysOnMarket)}
                  </div>
                </td>
                {showActionsColumn && (
                  <td
                    data-pdf-ignore="true"
                    className="sticky right-0 border-l border-gray-100 bg-white px-3 py-3 group-hover:bg-gray-50"
                  >
                    <div className="flex flex-col items-start gap-2">
                    {showDetailLinks && property.propertyId && (
                      <a
                        href={`/detail/${encodeURIComponent(property.propertyId)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Detail
                      </a>
                    )}
                    {property.propertyId && onIgnoreProperty && (
                      <button
                        type="button"
                        disabled={Boolean(recalculatingPropertyId)}
                        onClick={() => onIgnoreProperty(property.propertyId)}
                        className="text-red-600 hover:text-red-800 disabled:cursor-wait disabled:text-gray-400"
                      >
                        {recalculatingPropertyId === property.propertyId ? 'Přepočítávám…' : 'Ignorovat'}
                      </button>
                    )}
                    {isSimilarityMethod(calculationMethod) && property.propertyId && onWeightOverride && (
                      <div className="mt-1 flex w-36 flex-wrap gap-1">
                        <button
                          type="button"
                          title="Snížit použitou váhu o 10 procentních bodů"
                          disabled={Boolean(recalculatingPropertyId)}
                          onClick={() => onWeightOverride(property, 'decrease')}
                          className="rounded border border-gray-300 px-2 py-1 text-[10px] font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-wait disabled:text-gray-400"
                        >
                          −10 %
                        </button>
                        <button
                          type="button"
                          title="Zvýšit použitou váhu o 10 procentních bodů"
                          disabled={Boolean(recalculatingPropertyId)}
                          onClick={() => onWeightOverride(property, 'increase')}
                          className="rounded border border-gray-300 px-2 py-1 text-[10px] font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-wait disabled:text-gray-400"
                        >
                          +10 %
                        </button>
                        <button
                          type="button"
                          title="Nastavit plnou ruční váhu"
                          disabled={Boolean(recalculatingPropertyId)}
                          onClick={() => onWeightOverride(property, 'force')}
                          className="rounded border border-blue-300 px-2 py-1 text-[10px] font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-wait disabled:text-gray-400"
                        >
                          Vynutit 100 %
                        </button>
                        {Number.isFinite(property.manualWeightOverride) && (
                          <button
                            type="button"
                            title="Zrušit ruční váhu a znovu použít automatický výpočet"
                            disabled={Boolean(recalculatingPropertyId)}
                            onClick={() => onWeightOverride(property, 'auto')}
                            className="rounded border border-gray-300 px-2 py-1 text-[10px] font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-wait disabled:text-gray-400"
                          >
                            Auto
                          </button>
                        )}
                        {recalculatingPropertyId === `weight:${property.propertyId}` && (
                          <span className="w-full text-[10px] text-gray-500">Přepočítávám…</span>
                        )}
                      </div>
                    )}
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
        </table>
      </div>
      <LockedComparableRows count={lockedComparablesCount} />
    </div>
  )
}

export default ComparableTable
